import { ReactNode } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronRight, Close, SellOutlined } from "@mui/icons-material";
import { useAppPathname } from "@/routing/navigation";
import getPageContentContainer from "./pageContentContainer";

type PromoBannerProps = {
  message: ReactNode;
  ctaLabel: string;
  onCtaClick: () => void;
  onDismiss: () => void;
};

/**
 * Reusable promotional banner rendered below the app bar, aligned with the
 * page content container — including pages whose container is narrower than
 * the default layout (see pageContentContainer.ts). Presentation only —
 * audience gating, experiment flags, and dismissal persistence belong to the
 * wrapper (see AbandonedCheckoutPromoBanner).
 */
export default function PromoBanner({
  message,
  ctaLabel,
  onCtaClick,
  onDismiss,
}: PromoBannerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const pageContainer = getPageContentContainer(useAppPathname());

  return (
    <Box data-testid="promo-banner">
      {/* Full-width block wrapper so the centered inner Box (mirroring the
          page content container in Drawer.tsx) is not laid out as a flex
          child of the parent banners Stack, which drops mx auto. */}
      <Box sx={{ width: "100%", maxWidth: "1360px", mx: "auto", px: 3, pt: 3 }}>
        {/* Second mirror layer: pages with a narrower content container get
            the banner at that container's width and alignment. */}
        <Box
          data-testid="promo-banner-page-container"
          sx={
            pageContainer
              ? {
                  maxWidth: pageContainer.maxWidth,
                  mx: pageContainer.centered ? "auto" : 0,
                }
              : undefined
          }
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              bgcolor: "promoBanner.background",
              borderRadius: 2,
              px: 2,
              py: isMobile ? 1 : 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={isMobile ? 2 : 3}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={isMobile ? 2 : 1}
              >
                {!isMobile && (
                  <SellOutlined sx={{ fontSize: 16, color: "common.white" }} />
                )}
                <Typography variant="body1" sx={{ color: "common.white" }}>
                  {message}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                size="small"
                onClick={onCtaClick}
                endIcon={isMobile ? undefined : <ChevronRight />}
                data-testid="promo-banner-cta"
                sx={{
                  flexShrink: 0,
                  bgcolor: "promoBanner.ctaBackground",
                  color: "common.black",
                  "&:hover": { bgcolor: "promoBanner.ctaHoverBackground" },
                }}
              >
                {ctaLabel}
              </Button>
            </Stack>
            <IconButton
              size="small"
              aria-label="Dismiss"
              onClick={onDismiss}
              data-testid="promo-banner-dismiss"
              sx={{ color: "common.white" }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
